import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import SearchableSelect from '../../components/ui/SearchableSelect';
import useTranslation from '../../hooks/useTranslation';
import { useAuth } from '../../auth/AuthProvider';
import { isAdmin } from '../../utils/permissions';
import { projectService } from '../../services/projectService';
import { getBrainSignals, COLUMNS, VARS_BY_VIEW } from '../../services/brainService';
import { formatCurrency } from '../../utils/formatCurrency';

// ─── Helpers ────────────────────────────────────────────────
const fmt = (val) => formatCurrency(val);

/**
 * Ou vit reellement chaque chiffre.
 *
 * Deux liens profonds seulement existent dans l'app :
 *   ExtrasPage lit ?highlight=<extraId>            (ExtrasPage.jsx:836)
 *   Avenants  lit ?subcontract=<subcontractId>     (change-orders:173)
 * Partout ailleurs on ouvre LA PAGE avec le projet. On ne fabrique pas un
 * lien profond qui n'existe pas : il serait ignore en silence.
 */
const STEPS_BY_SIGNAL = {
  S17: [
    { key: 'subInvoices', to: (s) => `/project/${s?.project_id}/sub-invoices` },
    { key: 'clientInvoices', to: (s) => `/project/${s?.project_id}/client-invoices` },
  ],
  contract_over_budget: [
    { key: 'budgetLine', to: (s) => `/project/${s?.project_id}/budget` },
    { key: 'contract', to: (s) => `/project/${s?.project_id}/contracts` },
  ],
  tm_budget_watch: [
    { key: 'contract', to: (s) => `/project/${s?.project_id}/contracts` },
    { key: 'budgetLine', to: (s) => `/project/${s?.project_id}/budget` },
  ],
  contract_unsigned: [
    { key: 'project', to: (s) => `/vue-projet/${s?.project_id}` },
    { key: 'contract', to: (s) => `/project/${s?.project_id}/contracts` },
  ],
  unassigned_budget_line: [
    { key: 'budgetLine', to: (s) => `/project/${s?.project_id}/budget` },
    { key: 'contracts', to: (s) => `/project/${s?.project_id}/contracts` },
  ],
  capture_gap: [
    { key: 'project', to: (s) => `/vue-projet/${s?.project_id}` },
    { key: 'dailyLog', to: (s) => `/project/${s?.project_id}/daily-log` },
  ],
  task_stall: [
    { key: 'dailyLog', to: (s) => `/project/${s?.project_id}/daily-log` },
    { key: 'project', to: (s) => `/vue-projet/${s?.project_id}` },
  ],
  extra_no_change_order: [
    // Lien profond REEL : ExtrasPage met en evidence l'extra vise.
    { key: 'extras', to: (s) => `/project/${s?.project_id}/extras?highlight=${s?.entity_id}` },
    { key: 'changeOrders', to: (s) => `/project/${s?.project_id}/change-orders` },
  ],
  paid_unreleased: [
    // ContractsPage ne lit aucun identifiant en parametre : on ouvre la page.
    // QuittancesPage lit ?tab=quittances. La denonciation n'apparait que si
    // elle existe (champ `when`, optionnel, filtre plus bas).
    { key: 'contract', to: (s) => `/project/${s?.project_id}/contracts` },
    {
      key: 'denonciation',
      to: (s) => `/project/${s?.project_id}/quittances`,
      when: (s) => s?.evidence?.denonciation_exists === true,
    },
    { key: 'quittances', to: (s) => `/project/${s?.project_id}/quittances?tab=quittances` },
  ],
};

const SEVERITY_DOT = { red: 'bg-red-500', amber: 'bg-amber-500', yellow: 'bg-yellow-400' };
const SEVERITY_BORDER = { red: '#EF4444', amber: '#F59E0B', yellow: '#FACC15' };
const COLUMN_ICON = { finances: 'DollarSign', legal: 'Scale', documents: 'FileText' };

const fmtDate = (d, language) => {
  if (!d) return '—';
  return new Date(`${String(d)?.slice(0, 10)}T12:00:00`)?.toLocaleDateString(
    language === 'en' ? 'en-CA' : 'fr-CA',
    { year: 'numeric', month: 'short', day: 'numeric' }
  );
};

// Le montant expose est extrait par le service, par vue. La page ne devine
// plus le nom de la colonne.

// ─── Page ───────────────────────────────────────────────────
const BrainPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { userProfile } = useAuth();

  const [allProjects, setAllProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Liste des projets, pour le selecteur ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await projectService?.getAll();
        if (!cancelled) setAllProjects(rows || []);
      } catch (err) {
        console.error('[BrainPage] projects load failed:', err);
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Signaux — les huit vues, une seule fois ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await getBrainSignals();
        if (!cancelled) setData(result);
      } catch (err) {
        console.error('[BrainPage] signals load failed:', err);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Ce qui NOMME une ligne. task_stall ne porte ni project_number ni
   * project_name : on les resout depuis la liste des projets. Sans ca, neuf
   * lignes identiques a l'ecran, impossibles a distinguer.
   */
  const captionOf = useCallback(
    (signal) => {
      const parts = [];
      if (signal?.label) parts?.push(signal?.label);
      if (!projectId) {
        const p = (allProjects || [])?.find((x) => x?.id === signal?.project_id);
        const proj = p?.projectNumber || p?.name;
        if (proj) parts?.push(proj);
      }
      return parts?.join(' · ');
    },
    [allProjects, projectId]
  );

  const handleProjectChange = (id) => {
    if (id) navigate(`/brain/${id}`);
  };

  const project = useMemo(
    () => (allProjects || [])?.find((p) => p?.id === projectId) || null,
    [allProjects, projectId]
  );

  // ── Perimetre : le projet choisi, sinon tout ──
  const scoped = useMemo(() => {
    const signals = data?.signals || [];
    const blind = data?.blind || [];
    if (!projectId) return { signals, blind };
    return {
      signals: signals?.filter((s) => s?.project_id === projectId),
      blind: blind?.filter((s) => s?.project_id === projectId),
    };
  }, [data, projectId]);

  const byColumn = useMemo(() => {
    const map = {};
    for (const c of COLUMNS) map[c] = [];
    for (const s of scoped?.signals || []) {
      // `column` est pose par le service ; le repli couvre un signal_code
      // inconnu plutot que de le faire disparaitre.
      (map?.[s?.column] || map?.documents)?.push(s);
    }
    return map;
  }, [scoped]);

  // Cartes depliees, par cle de signal.
  const [expanded, setExpanded] = useState({});
  const toggleExpanded = useCallback(
    (key) => setExpanded((prev) => ({ ...prev, [key]: !prev?.[key] })),
    []
  );

  const kpis = useMemo(() => {
    const list = scoped?.signals || [];
    const critical = list?.filter((s) => s?.severity === 'red')?.length || 0;
    // Garde != null avant toute somme : formatCurrency rendrait "0,00 $"
    // sur un null, indistinct d'un vrai zero.
    const exposed = list?.reduce(
      (sum, s) => (s?.amount != null ? sum + s?.amount : sum),
      0
    );
    const sites = new Set(list?.map((s) => s?.project_id)?.filter(Boolean))?.size;
    return { total: list?.length || 0, critical, exposed, sites };
  }, [scoped]);

  /**
   * La phrase vient de i18n, jamais du code. Les colonnes de preuve servent
   * de variables. Cle absente -> on montre le rule_code brut plutot qu'une
   * ligne vide : un signal muet est pire qu'un signal mal formule.
   */
  /**
   * Traduit les colonnes de preuve en variables du gabarit. Les vues rendent
   * contract_value / pct_max / days_since_start ; les phrases parlent en
   * {value} / {pctMax} / {daysSite}. Sans cette etape, les accolades
   * s'affichaient en clair.
   */
  const varsOf = useCallback(
    (signal) => {
      const map = VARS_BY_VIEW?.[signal?.view] || {};
      const out = {};
      for (const [name, [col, kind]] of Object.entries(map)) {
        const raw = signal?.evidence?.[col];
        if (raw == null) continue;   // laisse le gabarit montrer {name} plutot qu'un faux zero
        out[name] =
          kind === 'money' ? fmt(raw)
          : kind === 'date' ? fmtDate(raw, language)
          // Statut d'une DP : son libelle, pas le code de la base.
          : kind === 'dpStatus' ? t(`paymentRequests.status.${raw}`)
          : raw;
      }
      return out;
    },
    [language, t]
  );

  const sentenceOf = useCallback(
    (signal) => {
      const key = `brain.signals.${signal?.rule_code}`;
      const out = t(key, varsOf(signal));
      return out === key ? signal?.rule_code : out;
    },
    [t, varsOf]
  );

  const silenceOf = useCallback(
    (signal) => {
      const key = `brain.silence.${signal?.reason_code}`;
      const out = t(key, varsOf(signal));
      return out === key ? signal?.reason_code : out;
    },
    [t, varsOf]
  );

  if (!userProfile || !isAdmin(userProfile)) return null;

  if (loading || projectsLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="pt-0 pb-0 px-0">

      {/* ── En-tete — meme structure que project360 ── */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">

          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="BrainCircuit" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-[#2C3E50]">{t('brain.title')}</h1>
            </div>
            {project && (
              <div className="flex items-center gap-1 mb-1">
                <span className="text-sm text-primary font-normal">
                  {project?.projectNumber || ''}
                </span>
                {project?.projectNumber && (
                  <span className="text-sm text-primary font-normal">-</span>
                )}
                <span className="text-sm text-primary font-normal">{project?.name || ''}</span>
              </div>
            )}
            <p className="text-xs text-gray-500">{t('brain.subtitle')}</p>
          </div>

          <div className="flex-1 flex flex-col items-end gap-2">
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <StatsCard
                title={t('brain.kpiSignals')}
                value={kpis?.total}
                iconName="Radio"
                iconColor="text-blue-500"
                valueColor="text-blue-600"
                titleWeight="font-medium"
                sublabel=""
              />
              <StatsCard
                title={t('brain.kpiCritical')}
                value={kpis?.critical}
                iconName="AlertTriangle"
                iconColor={kpis?.critical > 0 ? 'text-red-500' : 'text-gray-400'}
                valueColor={kpis?.critical > 0 ? 'text-red-600' : 'text-gray-900'}
                titleWeight="font-semibold"
                sublabel=""
              />
              <StatsCard
                title={t('brain.kpiExposed')}
                value={kpis?.exposed > 0 ? fmt(kpis?.exposed) : '—'}
                iconName="DollarSign"
                iconColor="text-amber-500"
                valueColor="text-amber-700"
                titleWeight="font-medium"
                sublabel=""
              />
              <StatsCard
                title={t('brain.kpiSites')}
                value={kpis?.sites}
                iconName="Building2"
                iconColor="text-purple-500"
                valueColor="text-purple-600"
                titleWeight="font-medium"
                sublabel=""
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Selecteur de projet — meme traitement que project360 ── */}
      <div className="mb-4 flex items-center gap-2">
        <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
          {t('brain.selectorLabel')}
        </label>
        <SearchableSelect
          options={allProjects?.map((p) => ({
            value: p?.id,
            label: p?.projectNumber ? `${p?.projectNumber} · ${p?.name}` : p?.name,
          }))}
          value={projectId || ''}
          onChange={handleProjectChange}
          placeholder={t('brain.selectorPlaceholder')}
          disabled={projectsLoading}
          className="max-w-[320px] w-full"
        />
      </div>

      {/* ── Trois colonnes : un empilement de cartes, pas des boites ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {COLUMNS?.map((col) => (
          <div key={col} className="min-w-0">

            {/* En-tete de colonne */}
            <div className="flex items-center gap-2 mb-2 px-0.5">
              <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                <Icon name={COLUMN_ICON?.[col]} size={12} className="text-gray-500" />
              </span>
              <div className="min-w-0">
                <h3 className="text-[12px] font-semibold text-[#2C3E50] uppercase tracking-wide leading-none">
                  {t(`brain.columns.${col}`)}
                </h3>
                <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                  {t(`brain.columns.${col}Sub`)}
                </p>
              </div>
              <span className="ml-auto text-[11px] font-semibold text-gray-400 tabular-nums shrink-0">
                {byColumn?.[col]?.length || 0}
              </span>
            </div>

            {/* Une colonne vide garde son en-tete : l'absence est une reponse. */}
            {byColumn?.[col]?.length === 0 ? (
              <div className="border border-gray-200 rounded-lg bg-white shadow-sm px-3 py-4">
                <p className="text-xs text-gray-400 italic">{t('brain.empty')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {byColumn?.[col]?.map((s) => {
                  const isOpen = !!expanded?.[s?.key];
                  const steps = (STEPS_BY_SIGNAL?.[s?.signal_code] || [])
                    ?.filter((step) => !step?.when || step?.when(s));
                  // t() rend la CLE quand elle manque (LanguageContext:43,46).
                  // On compare donc au lieu de tester la nullite, et on exige
                  // une chaine : une cle intermediaire rendrait un objet.
                  const whyKey = `brain.why.${s?.rule_code}`;
                  const why = t(whyKey);
                  const hasWhy = typeof why === 'string' && why !== whyKey;
                  return (
                    <div
                      key={s?.key}
                      className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden"
                      style={{ borderLeftWidth: 3, borderLeftColor: SEVERITY_BORDER?.[s?.severity] || '#D1D5DB' }}
                    >
                      {/* Ligne du haut : severite · regle · chiffre */}
                      <div className="flex items-center gap-2 px-3 pt-2.5">
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                            SEVERITY_DOT?.[s?.severity] || 'bg-gray-300'
                          }`}
                        />
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide truncate min-w-0">
                          {captionOf(s) || s?.rule_code}
                        </span>
                        {s?.amount != null && (
                          <span
                            className="ml-auto text-xs font-semibold text-gray-900 shrink-0"
                            style={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {fmt(s?.amount)}
                          </span>
                        )}
                      </div>

                      <p className="px-3 pt-1.5 pb-2.5 text-xs text-gray-800 leading-relaxed">
                        {sentenceOf(s)}
                      </p>

                      <button
                        type="button"
                        onClick={() => toggleExpanded(s?.key)}
                        aria-expanded={isOpen}
                        className="w-full flex items-center justify-center gap-1 px-3 py-1.5 border-t border-gray-100 text-[10px] font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <Icon
                          name="ChevronDown"
                          size={11}
                          className={`transition-transform motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`}
                        />
                        {isOpen ? t('brain.collapse') : t('brain.expand')}
                      </button>

                      {/* `hidden` plutot que style.display : l'attribut reste
                          lisible par les technologies d'assistance. */}
                      <div hidden={!isOpen} className="px-3 py-2.5 bg-gray-50 border-t border-gray-100">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                          {t('brain.pathTitle')}
                        </p>
                        <div className="space-y-1">
                          {steps?.map((step, i) => (
                            <button
                              key={step?.key}
                              type="button"
                              onClick={() => navigate(step?.to(s))}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md bg-white border border-gray-200 text-left hover:border-primary hover:bg-orange-50/40 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                            >
                              <span className="w-4 h-4 rounded bg-gray-100 text-[9px] font-semibold text-gray-500 flex items-center justify-center shrink-0">
                                {i + 1}
                              </span>
                              <span className="flex-1 text-[11px] text-gray-700 truncate">
                                {t(`brain.steps.${step?.key}`)}
                              </span>
                              <Icon name="ArrowUpRight" size={11} className="text-gray-400 shrink-0" />
                            </button>
                          ))}
                          {/* 3e pas : le fait calcule. Rien a ouvrir.
                              Absent pour paid_unreleased : le chemin s'arrete aux quittances. */}
                          {s?.signal_code !== 'paid_unreleased' && (
                            <div className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md bg-white border border-orange-200">
                              <span className="w-4 h-4 rounded bg-orange-100 text-[9px] font-semibold text-primary flex items-center justify-center shrink-0">
                                {steps?.length + 1}
                              </span>
                              <span className="flex-1 text-[11px] font-medium text-primary truncate">
                                {s?.amount != null ? fmt(s?.amount) : sentenceOf(s)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Pourquoi ca compte. Tout le bloc disparait si la
                            phrase manque : un titre seul ne vaut rien, et une
                            cle brute a l'ecran vaut moins que rien. */}
                        {hasWhy && (
                          <div className="mt-3">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                              {t('brain.whyTitle')}
                            </p>
                            <p className="text-xs text-gray-700 leading-relaxed">{why}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Ce que Brain ne peut pas evaluer ── */}
      <div className="mt-3 border border-gray-200 rounded-lg bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Icon name="HelpCircle" size={14} className="text-gray-400" />
            <h3 className="text-[12px] font-semibold text-[#2C3E50] uppercase tracking-wide">
              {t('brain.blindTitle')}
            </h3>
          </div>
          {scoped?.blind?.length > 0 && (
            <span className="text-[11px] font-semibold text-gray-400 tabular-nums">
              {scoped?.blind?.length}
            </span>
          )}
        </div>
        <div className="px-4 py-2 divide-y divide-gray-100 max-h-[280px] overflow-y-auto">
          {scoped?.blind?.length === 0 ? (
            <p className="text-xs text-gray-400 italic py-3">{t('brain.empty')}</p>
          ) : (
            scoped?.blind?.map((s) => (
              <div key={s?.key} className="flex items-start gap-2 py-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-gray-300" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-800 leading-snug">{silenceOf(s)}</p>
                  {captionOf(s) && (
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                      {captionOf(s)}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default BrainPage;
